import { Button, Input, Typography } from '@breetometer/components';
import { classNameMerger } from '@breetometer/utils';
import { Checkbox, ModalParts } from 'BreetComponents';
import {
	handleApiError,
	type IDropDownItem,
	type ITeamMembersRole,
	onFormPreventDefault,
	TEAM_MEMBER_ROLE_AND_PERMISSIONS,
} from 'BreetHelpers';
import { useFormData } from 'BreetHooks';
import { useInviteTeamMemberMutation } from 'BreetRedux';
import { type MouseEvent, useCallback } from 'react';

import DropDown from '../Dropdown/Dropdown';
import {
	type IInviteTeamMemberChild,
	InviteTeamMemberSchema,
	type InviteTeamMemberSchemaType,
	roleOptions,
	statusOptions,
} from './DialogInviteTeamMember.helpers';

const Child = ({ teamMemberData, modalApis }: IInviteTeamMemberChild) => {
	const [inviteTeamMemberApi, { isLoading }] = useInviteTeamMemberMutation();

	const { closeModalDirect, getModalProps, handleOpenModal } = modalApis;
	const modalProps = getModalProps('inviteTeamMember');
	const modalMetadata = modalProps?.metadata as { intent?: 'invite' | 'view'; teamMemberId?: string } | undefined;

	const inviteTeamMemberFormData = useFormData(
		InviteTeamMemberSchema,
		{
			email: teamMemberData?.data?.email,
			fullName: teamMemberData?.data?.fullName,
			role: teamMemberData?.data?.role ?? 'developer',
		},
		{ mode: 'onChange' }
	);

	const handleSelectRole = useCallback(
		(item: IDropDownItem) => (e: MouseEvent<HTMLButtonElement>) => {
			e.stopPropagation();
			inviteTeamMemberFormData.hookFormProps.setValue('role', item.value, { shouldValidate: true });
		},
		[inviteTeamMemberFormData.hookFormProps]
	);

	const inviteTeamMember = useCallback(
		(data: InviteTeamMemberSchemaType) => {
			inviteTeamMemberApi({
				email: data.email,
				fullName: data.fullName,
				role: data.role,
			})
				.unwrap()
				.then(() => {
					closeModalDirect('inviteTeamMember');
				})
				.catch(handleApiError);
		},
		[inviteTeamMemberApi, closeModalDirect]
	);

	const selectedRoleItem = roleOptions.find((option) => option.value === inviteTeamMemberFormData.hookFormProps.watch('role'));
	const selectedStatusItem = statusOptions.find((option) => option.value === teamMemberData?.data?.status);

	const modalTitle = modalMetadata?.intent === 'invite' ? 'Invite Team Member' : 'Team Member';

	const selectedRole = (
		modalMetadata?.intent === 'invite' ? inviteTeamMemberFormData.hookFormProps.watch('role') : teamMemberData?.data?.role
	) as ITeamMembersRole | undefined;

	const permissionOptions = selectedRole ? TEAM_MEMBER_ROLE_AND_PERMISSIONS[selectedRole] : [];

	return (
		<ModalParts.Wrapper
			title={modalTitle}
			root={modalTitle}
			rightSheet
		>
			<div className={classNameMerger('invite-team-member', modalMetadata?.intent === 'view' && 'view-intent')}>
				{modalMetadata?.intent === 'invite' && (
					<Typography
						as='h4'
						className='color-primary-100'
					>
						Enter the details of your team member
					</Typography>
				)}

				<form
					className='invite-team-member-form new-drop-down-input'
					data-modal-ignore='true'
					onSubmit={onFormPreventDefault}
				>
					<Input
						id=''
						label='Full Name'
						widthType='full'
						required
						{...inviteTeamMemberFormData.hookFormProps.register('fullName')}
						errorMessage={inviteTeamMemberFormData.hookFormProps.formState.errors.fullName?.message}
					/>

					<Input
						id=''
						label='Email'
						widthType='full'
						required
						{...inviteTeamMemberFormData.hookFormProps.register('email')}
						errorMessage={inviteTeamMemberFormData.hookFormProps.formState.errors.email?.message}
					/>

					<DropDown.Default
						handleItemSelect={handleSelectRole}
						selectedItem={
							selectedRoleItem
								? {
										...selectedRoleItem,
										value: selectedRoleItem.name,
									}
								: undefined
						}
						label='Select Role'
						id='role'
						items={roleOptions}
						iconAs='image'
						placeholder='Select Role'
						hideValue
						errorMessage={inviteTeamMemberFormData.hookFormProps.formState.errors.role?.message}
					/>

					<div className='permission bg-primary-600 border-dark-navy'>
						<div className='permission-header'>
							<Typography className='color-tertiary-400'>Permissions</Typography>
						</div>
						<div className='permission-list'>
							{permissionOptions.map((item) => (
								<Checkbox
									key={item.label}
									checked
									disabled
								>
									<Typography className='color-neutral-50'>{item.label}</Typography>
								</Checkbox>
							))}
						</div>
					</div>

					{modalMetadata?.intent === 'view' && (
						<div className={classNameMerger('drop-down-status', teamMemberData?.data?.status)}>
							<DropDown.Default
								handleItemSelect={handleSelectRole}
								selectedItem={
									selectedStatusItem
										? {
												...selectedStatusItem,
												value: selectedStatusItem.name,
											}
										: undefined
								}
								label='Status'
								id='status'
								items={statusOptions}
								iconAs='image'
								placeholder='Status'
								hideValue
								errorMessage={inviteTeamMemberFormData.hookFormProps.formState.errors.role?.message}
							/>
						</div>
					)}

					<div className='cta-button'>
						<Button
							disabled={
								!inviteTeamMemberFormData.hookFormProps.formState.isValid ||
								!inviteTeamMemberFormData.hookFormProps.formState.isDirty
							}
							type='button'
							onClick={inviteTeamMemberFormData.hookFormProps.handleSubmit(inviteTeamMember)}
							isLoading={isLoading}
						>
							{modalMetadata?.intent === 'view' ? 'Save Changes' : 'Invite'}
						</Button>
					</div>

					{modalMetadata?.intent === 'view' && teamMemberData?.data?.role !== 'owner' && (
						<div className='cta-button-remove'>
							<Button
								variant='native'
								type='button'
								onClick={handleOpenModal({ id: 'deleteTeamMember', metadata: { teamMemberId: teamMemberData?.data?.id } })}
							>
								Remove Team Member
							</Button>
						</div>
					)}
				</form>
			</div>
		</ModalParts.Wrapper>
	);
};

export default Child;
